import React from 'react';
import { motion } from 'framer-motion';

// Enso (zen circle) — calligraphic incomplete circle
export const Enso = ({ className = '', stroke = 'currentColor', size = 200 }) => (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} aria-hidden="true">
        <path
            d="M 100 20
               C 50 20, 20 60, 20 100
               C 20 145, 55 180, 100 180
               C 145 180, 180 145, 180 100
               C 180 70, 160 40, 130 28"
            fill="none"
            stroke={stroke}
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.85"
        />
    </svg>
);

// Animated Enso that draws on view
export const EnsoAnimated = ({ className = '', stroke = 'currentColor', size = 200, delay = 0 }) => (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} aria-hidden="true">
        <motion.path
            d="M 100 20
               C 50 20, 20 60, 20 100
               C 20 145, 55 180, 100 180
               C 145 180, 180 145, 180 100
               C 180 70, 160 40, 130 28"
            fill="none"
            stroke={stroke}
            strokeWidth="2.5"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 0.9 }}
            viewport={{ once: true }}
            transition={{ duration: 2.4, delay, ease: [0.22, 1, 0.36, 1] }}
        />
    </svg>
);

// Mt. Fuji line silhouette
export const FujiLines = ({ className = '', stroke = 'currentColor' }) => (
    <svg viewBox="0 0 800 280" className={className} aria-hidden="true" preserveAspectRatio="xMidYMax meet">
        <g fill="none" stroke={stroke} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            {/* main fuji */}
            <path d="M 100 240 L 280 90 Q 295 75, 320 80 Q 360 86, 400 90 L 580 240" opacity="0.9" />
            {/* snow line */}
            <path d="M 240 130 Q 280 110, 305 115 Q 360 124, 400 130 Q 430 134, 460 135" opacity="0.6" />
            {/* second peak (smaller) */}
            <path d="M 540 240 L 640 160 Q 660 150, 680 158 L 760 240" opacity="0.4" />
            {/* horizon */}
            <line x1="0" y1="240" x2="800" y2="240" opacity="0.3" />
            {/* mist lines */}
            <line x1="60" y1="200" x2="200" y2="200" opacity="0.25" strokeDasharray="2 6" />
            <line x1="220" y1="215" x2="380" y2="215" opacity="0.25" strokeDasharray="2 6" />
            <line x1="420" y1="205" x2="560" y2="205" opacity="0.2" strokeDasharray="2 6" />
        </g>
    </svg>
);

// Brush stroke divider
export const BrushDivider = ({ className = '', color = '#1A1A1A' }) => (
    <svg viewBox="0 0 600 18" className={className} aria-hidden="true" preserveAspectRatio="none">
        <path
            d="M 4 9 C 80 4, 160 14, 280 9 S 460 5, 596 10"
            fill="none"
            stroke={color}
            strokeWidth="1.2"
            strokeLinecap="round"
            opacity="0.7"
        />
        <path
            d="M 12 11 C 90 8, 200 12, 320 10 S 480 8, 588 11"
            fill="none"
            stroke={color}
            strokeWidth="0.6"
            strokeLinecap="round"
            opacity="0.4"
        />
    </svg>
);

// Path / Nakasendo line abstract
export const PathLine = ({ className = '', stroke = 'currentColor' }) => (
    <svg viewBox="0 0 400 600" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid meet">
        <g fill="none" stroke={stroke} strokeWidth="1" strokeLinecap="round">
            <path
                d="M 200 30 Q 120 100, 180 200 T 200 350 Q 280 420, 200 500 T 200 580"
                opacity="0.45"
            />
            {/* milestones */}
            <circle cx="200" cy="30" r="3" fill={stroke} opacity="0.7" />
            <circle cx="160" cy="180" r="2" fill={stroke} opacity="0.5" />
            <circle cx="200" cy="350" r="2" fill={stroke} opacity="0.5" />
            <circle cx="240" cy="450" r="2" fill={stroke} opacity="0.5" />
            <circle cx="200" cy="580" r="3" fill={stroke} opacity="0.7" />
        </g>
    </svg>
);

// Bonsai abstract icon
export const BonsaiMark = ({ className = '', stroke = 'currentColor', size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} aria-hidden="true">
        <g fill="none" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            {/* pot */}
            <path d="M 28 78 L 32 88 L 68 88 L 72 78 Z" />
            <line x1="28" y1="78" x2="72" y2="78" />
            {/* trunk */}
            <path d="M 50 78 C 48 65, 46 55, 44 48 C 42 40, 48 35, 52 32" />
            {/* canopy clouds */}
            <ellipse cx="42" cy="40" rx="14" ry="6" />
            <ellipse cx="58" cy="32" rx="12" ry="5" />
            <ellipse cx="50" cy="22" rx="10" ry="4" />
        </g>
    </svg>
);

// Logo / monogram
export const NakasendoMark = ({ className = '', color = 'currentColor' }) => (
    <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
        <g fill={color}>
            <rect x="2" y="19" width="36" height="1.5" />
            <rect x="19" y="2" width="1.5" height="36" />
            <circle cx="20" cy="20" r="9" fill="none" stroke={color} strokeWidth="1.2" />
            <circle cx="20" cy="20" r="2" />
        </g>
    </svg>
);
